import {
  InputField,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  MultiSelectField,
  MultiSelectOption,
  SingleSelectField,
  SingleSelectOption,
  Transfer,
} from "@dhis2/ui";
import { faGear, faTable } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton } from "@mui/material";
import _ from "lodash";
import { shallow } from "zustand/shallow";

import {
  convertDataByDataType,
  debounce,
  downloadExcel,
  filterOptionsByTrackedEntityAttribute,
  getCurrentDateFormatted,
  pickTranslation,
} from "@/config/utils";

import useMetadataStore from "@/state/metadata";
import useDashboardStore from "@/state/dashboard";

import AccordionGroup from "./AccordionGroup";
import Accordion from "./Accordion";
import OrgUnitSelector from "./CustomOrgUnitSelector";

import { INIT_MASTER_FACILITIES_LIST_STATE } from "../..";

const INIT_FILTER = {
  hfid: "",
  facilityName: "",
  location: [],
  classification: [],
  locationType: [],
  type: [],
  ownership: [],
};

const INIT_SORT = {
  field: "",
  status: "",
};

const LIST_FILTER_FIELD = [
  {
    field: "hfid",
    type: "TEXT",
    teaId: "qs7l1gtT608",
    pickTranslationFrom: "formName",
  },
  {
    field: "facilityName",
    teaIds: ["CsjieOOZ9pv", "NsOTMYIp3qX"],
    type: "TEXT",
  },
  {
    field: "classification",
    type: "SELECT",
    teaId: "JBAZAnZK4EL",
    pickTranslationFrom: "formName",
  },
  {
    field: "locationType",
    type: "SELECT",
    teaId: "qm3FM7zcLWi",
    pickTranslationFrom: "formName",
  },
  {
    field: "type",
    type: "SELECT",
    teaId: "KVaIZ8B0a1B",
    pickTranslationFrom: "formName",
  },
  {
    field: "ownership",
    type: "SELECT",
    teaId: "Lazma6Rapgs",
    pickTranslationFrom: "formName",
  },
];

const OU_CODE_ID = "mbSrXdMqlOv";

const TransferHeader = ({ label }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        color: "#4A5768",
        fontSize: "14px",
        height: "30px",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
      }}
    >
      {t(label)}
    </Box>
  );
};

const ConfigControl = () => {
  const { t, i18n } = useTranslation();
  const { serviceDeliveryProgram, orgUnits } = useMetadataStore(
    (state) => ({
      serviceDeliveryProgram: state.oupProgram,
      orgUnits: state.oupOrgUnits,
    }),
    shallow
  );
  const { changeAdditionalStateProperty, additionalState } = useDashboardStore(
    (state) => ({
      changeAdditionalStateProperty: state.changeAdditionalStateProperty,
      additionalState: state.additionalState,
    }),
    shallow
  );
  const {
    masterFacilitiesListState = INIT_MASTER_FACILITIES_LIST_STATE,
    dashboard8Data: data,
  } = additionalState;
  const { columns, filter, sort, paging } = masterFacilitiesListState;
  const { programTrackedEntityAttributes, trackedEntityAttributes } =
    serviceDeliveryProgram;
  //local state
  const [controlDialog, setControlDialog] = useState(false);
  const [tempTextFieldValue, setTempTextFiledValue] = useState({});
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [filterData, setFilterData] = useState(filter || INIT_FILTER);
  const [sortData, setSortData] = useState(sort || INIT_SORT);

  const allColumns = useMemo(
    () => [
      ...[
        { id: "longitude", type: "Attribute", name: t("longitude") },
        { id: "latitude", type: "Attribute", name: t("latitude") },
      ],
      ...programTrackedEntityAttributes.map((ptea) => {
        const foundAttr = trackedEntityAttributes.find(
          (tea) => tea.id === ptea.trackedEntityAttribute.id
        );
        return {
          id: foundAttr.id,
          type: "Attribute",
          name: foundAttr.name,
        };
      }),
    ],
    []
  );

  const handleTextFieldChange = useCallback(
    debounce((value, field) => {
      setFilterData((prevFilterData) => ({
        ...prevFilterData,
        [field]: value,
      }));
      setTempTextFiledValue({});
    }, 300),
    []
  );

  const handleSelectValueChange = useCallback(
    (value, field) =>
      setFilterData((prevFilterData) => ({
        ...prevFilterData,
        [field]: value,
      })),
    []
  );

  const applyControlConfigs = () => {
    const newMasterFacilitiesListState = {
      ...masterFacilitiesListState,
      columns: selectedColumns || columns,
      filter: filterData,
      sort: sortData,
      paging: { ...paging, page: 1 },
    };
    changeAdditionalStateProperty(
      "masterFacilitiesListState",
      newMasterFacilitiesListState
    );
    const newFilteredData = data.filter((item) => {
      let valid = true;
      const listKey = Object.keys(filterData);
      for (const filterDataItem of listKey) {
        if (filterData[filterDataItem].length === 0) continue;

        if (filterDataItem === "location") {
          const locations = filterData[filterDataItem];
          const provinces = locations
            .filter((location) => location.split("/").length === 3)
            .map((location) => location.split("/")[2]);
          const districts = locations
            .filter((location) => location.split("/").length === 4)
            .map((location) => location.split("/")[3]);

          if (
            !(
              provinces.includes(item["KatN02ohdCd"]) ||
              districts.includes(item["V9db007buZE"])
            )
          ) {
            valid = false;
          }
          continue;
        }

        const foundFilter = LIST_FILTER_FIELD.find(
          (filterItem) => filterItem.field === filterDataItem
        );
        if (!foundFilter) continue;

        const { teaId, type, teaIds } = foundFilter;

        if (teaId && type === "TEXT") {
          if (
            !item[teaId]
              .toLowerCase()
              .trim()
              .includes(filterData[filterDataItem].toLowerCase().trim())
          ) {
            valid = false;
          }
          continue;
        }

        if (teaId && type === "SELECT") {
          if (!filterData[filterDataItem].includes(item[teaId])) {
            valid = false;
          }
          continue;
        }

        if (teaIds) {
          const matchingTeaId = teaIds.find((id) => item[id] !== undefined);
          if (
            !matchingTeaId ||
            !item[matchingTeaId]
              .toLowerCase()
              .trim()
              .includes(filterData[filterDataItem].toLowerCase().trim())
          ) {
            valid = false;
          }
        }
      }

      if (valid) {
        return item;
      }
    });

    changeAdditionalStateProperty(
      "filteredData",
      newFilteredData.sort((a, b) => {
        if (!sortData.field || !sortData.status) return 0;
        if (sortData.status === "asc")
          return a[sortData.field] > b[sortData.field]
            ? 1
            : a[sortData.field] < b[sortData.field]
            ? -1
            : 0;
        else
          return a[sortData.field] < b[sortData.field]
            ? 1
            : a[sortData.field] > b[sortData.field]
            ? -1
            : 0;
      })
    );
    setControlDialog(false);
  };
  useEffect(() => {
    const newMasterFacilitiesListState = {
      ...masterFacilitiesListState,
      columns: [
        OU_CODE_ID,
        ...programTrackedEntityAttributes
          .filter((ptea) => {
            return (
              ptea.displayInList &&
              ptea.trackedEntityAttribute.id !== OU_CODE_ID
            );
          })
          .map((ptea) => {
            return ptea.trackedEntityAttribute.id;
          }),
        "longitude",
        "latitude",
      ],
    };
    changeAdditionalStateProperty(
      "masterFacilitiesListState",
      newMasterFacilitiesListState
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.getAttribute("data-test") === "dhis2-uicore-layer") {
        applyControlConfigs();
      }
    };
    if (controlDialog) {
      document
        .querySelector("div[data-test='dhis2-uicore-layer']")
        ?.addEventListener("click", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document
          .querySelector("div[data-test='dhis2-uicore-layer']")
          ?.removeEventListener("click", handleClickOutside);
      };
    }
  }, [
    controlDialog,
    JSON.stringify(selectedColumns),
    JSON.stringify(filterData),
    JSON.stringify(sortData),
  ]);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        "& .modal-action": {
          width: "100%",
        },
      }}
    >
      <IconButton
        sx={{ border: "0", backgroundColor: "transparent" }}
        disabled={!additionalState.dashboard8Ready}
        onClick={(e) => {
          setControlDialog(true);
        }}
      >
        <FontAwesomeIcon icon={faGear} style={{ fontSize: 22 }} />
      </IconButton>

      <Modal large position="center" hide={!controlDialog}>
        <ModalTitle>{t("control")}</ModalTitle>
        <ModalContent>
          <Box
            sx={{
              width: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              p: 3,
            }}
          >
            <AccordionGroup isMultipleOpen={false}>
              <Accordion title="columns">
                <Box sx={{ width: "100%" }}>
                  <Transfer
                    selected={selectedColumns || columns}
                    onChange={(value) => {
                      setSelectedColumns(value.selected);
                    }}
                    leftHeader={<TransferHeader label="availableColumns" />}
                    rightHeader={<TransferHeader label="selectedColumns" />}
                    filterable
                    className="!h-[350px]"
                    options={_.uniqBy(allColumns, "id").map((col) => {
                      return {
                        value: col.id,
                        label: `${col.type} - ${col.name}`,
                      };
                    })}
                    selectedWidth="100%"
                    optionsWidth="100%"
                  />
                </Box>
              </Accordion>
              <Accordion title="filter">
                <Box
                  sx={{
                    width: "100%",
                  }}
                >
                  <OrgUnitSelector
                    initialOrgUnit={filterData.location}
                    multiple
                    orgUnits={orgUnits.filter((ou) => {
                      const foundProvinceGroup = ou.organisationUnitGroups.find(
                        (oug) => oug.id === "jblbYwuvO33"
                      );
                      const foundDistrictGroup = ou.organisationUnitGroups.find(
                        (oug) => oug.id === "Zh1inFu0Z2O"
                      );
                      return foundProvinceGroup || foundDistrictGroup;
                    })}
                    accept={(value) => {
                      handleSelectValueChange(
                        value.map((item) => item.path),
                        "location"
                      );
                    }}
                    roots={orgUnits.filter((ou) => {
                      const foundProvinceGroup = ou.organisationUnitGroups.find(
                        (oug) => oug.id === "jblbYwuvO33"
                      );

                      return foundProvinceGroup;
                    })}
                    label={t("filterBy", { label: t("location") })}
                  />
                </Box>
                {LIST_FILTER_FIELD.map((filterField) => (
                  <Box key={filterField.field} sx={{ width: "100%" }}>
                    {filterField.type === "TEXT" ? (
                      <InputField
                        value={
                          tempTextFieldValue?.[filterField.field] !== undefined
                            ? tempTextFieldValue[filterField.field]
                            : filterData[filterField.field]
                        }
                        label={t("filterBy", {
                          label: filterField.pickTranslationFrom
                            ? pickTranslation(
                                trackedEntityAttributes.find(
                                  (tea) => tea.id === filterField.teaId
                                ),
                                i18n.language,
                                filterField.pickTranslationFrom
                              )
                            : t(filterField.field),
                        })}
                        onChange={(value) => {
                          setTempTextFiledValue({
                            [filterField.field]: value.value,
                          });
                          handleTextFieldChange(value.value, filterField.field);
                        }}
                      />
                    ) : (
                      <MultiSelectField
                        selected={filterData[filterField.field]}
                        label={t("filterBy", {
                          label: pickTranslation(
                            trackedEntityAttributes.find(
                              (tea) => tea.id === filterField.teaId
                            ),
                            i18n.language,
                            filterField.pickTranslationFrom
                          ),
                        })}
                        onChange={(value) =>
                          handleSelectValueChange(
                            value.selected,
                            filterField.field
                          )
                        }
                        filterable
                        clearable
                      >
                        {filterOptionsByTrackedEntityAttribute(
                          filterField.teaId,
                          serviceDeliveryProgram
                        ).map((option) => {
                          return (
                            <MultiSelectOption
                              key={option.id}
                              label={pickTranslation(
                                option,
                                i18n.language,
                                "name"
                              )}
                              value={option.code}
                            />
                          );
                        })}
                      </MultiSelectField>
                    )}
                  </Box>
                ))}
              </Accordion>
              <Accordion title="sort">
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    "& > .columns-selector": {
                      flex: "2 !important",
                    },
                    "& > .sort-status-selector": {
                      flex: "1",
                    },
                  }}
                >
                  <SingleSelectField
                    className="columns-selector"
                    filterable
                    clearable
                    label={t("columns")}
                    selected={sortData.field}
                    onChange={(value) => {
                      setSortData((prevSortData) => ({
                        ...prevSortData,
                        field: value.selected,
                      }));
                    }}
                  >
                    {_.uniqBy(allColumns, "id").map((column) => {
                      return (
                        <SingleSelectOption
                          key={column.id}
                          label={column.name}
                          value={column.id}
                        />
                      );
                    })}
                  </SingleSelectField>
                  <SingleSelectField
                    className="sort-status-selector"
                    clearable
                    label={t("sortStatus")}
                    selected={sortData.status}
                    onChange={(value) =>
                      setSortData((prevSortData) => ({
                        ...prevSortData,
                        status: value.selected,
                      }))
                    }
                  >
                    {["asc", "desc"].map((sortStatus) => {
                      return (
                        <SingleSelectOption
                          key={sortStatus}
                          label={t(sortStatus)}
                          value={sortStatus}
                        />
                      );
                    })}
                  </SingleSelectField>
                </Box>
              </Accordion>
            </AccordionGroup>
          </Box>
        </ModalContent>
        <ModalActions className="modal-action">
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              startIcon={
                <FontAwesomeIcon icon={faTable} style={{ fontSize: 22 }} />
              }
              onClick={() => {
                const data =
                  additionalState.filteredData ||
                  additionalState.dashboard8Data;
                const dataDownload = data.map((item) => {
                  return {
                    ...columns.reduce((prev, curr) => {
                      const foundAttribute = trackedEntityAttributes.find(
                        (tea) => tea.id === curr
                      );
                      return {
                        ...prev,
                        [foundAttribute
                          ? pickTranslation(
                              foundAttribute,
                              i18n.language,
                              "formName"
                            )
                          : t(curr)]: convertDataByDataType(item[curr], curr, {
                          ...serviceDeliveryProgram,
                          orgUnits,
                        }),
                      };
                    }, {}),
                  };
                });
                const fileName = `mfl-export-${getCurrentDateFormatted()}`;
                downloadExcel(dataDownload, fileName);
              }}
            >
              {t("downloadExcel")}
            </Button>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilterData(INIT_FILTER);
                  setSortData(INIT_SORT);
                  setSelectedColumns(null);
                  const newMasterFacilitiesListState = {
                    ...masterFacilitiesListState,
                    filter: INIT_FILTER,
                    sort: INIT_SORT,
                    paging: { ...paging, page: 1 },
                    columns: [
                      OU_CODE_ID,
                      ...programTrackedEntityAttributes
                        .filter((ptea) => {
                          return (
                            ptea.displayInList &&
                            ptea.trackedEntityAttribute.id !== OU_CODE_ID
                          );
                        })
                        .map((ptea) => {
                          return ptea.trackedEntityAttribute.id;
                        }),
                    ],
                  };
                  changeAdditionalStateProperty(
                    "masterFacilitiesListState",
                    newMasterFacilitiesListState
                  );
                  changeAdditionalStateProperty("filteredData", data);
                }}
              >
                {t("reset")}
              </Button>
              <Button variant="contained" primary onClick={applyControlConfigs}>
                {t("apply")}
              </Button>
            </Box>
          </Box>
        </ModalActions>
      </Modal>
    </Box>
  );
};

export default ConfigControl;
